import { Trans, t } from "@lingui/macro";
import type {
  DocumentExportFragment,
  EmailLogFragment,
} from "@src/__generated__/graphql";
import { TableBody } from "@src/components/modules/common/table";
import { ProformaNo } from "@src/components/modules/invoices/ProformaList/components/Table/Column/ProformaNo";
import { Card, ColoredAmount, formatDate } from "@src/components/ui-kit";
import { type TColumnDef, useTable } from "@src/components/ui-kit/table";
import { currency } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";

export interface TableProformaInvoicesData {
  id: string;
  invoice_no: string;
  issue_date: string;
  stats: { total_amount: number };
  abraflexi_exported?: boolean | null;
  documentExports: DocumentExportFragment[];
  emailLogs: EmailLogFragment[];
}

interface TableProformaInvoicesProps {
  data: TableProformaInvoicesData[];
  unusedAmount: number;
}

const TableProformaInvoices = ({
  data,
  unusedAmount,
}: TableProformaInvoicesProps) => {
  const { table } = useTable(data, cols);

  if (!data.length) {
    return null;
  }

  return (
    <div className="px-8 py-12">
      <div className="flex items-center mb-10">
        <span className="mr-2">
          <Trans>Unused amount:</Trans>
        </span>
        <span className="font-bold text-2xl">
          {currency.formatByWorkspace(unusedAmount)}
        </span>
      </div>
      <Card>
        <div className="pb-10">
          <div className="h-24 flex items-center justify-between px-6">
            <h2 className="text-2xl font-bold">
              <Trans>Proforma invoices</Trans>
            </h2>
          </div>
          <TableBody table={table} />
        </div>
      </Card>
    </div>
  );
};

const cols: TColumnDef<TableProformaInvoicesData, any> = [
  {
    key: "date",
    header: () => t`Date`,
    render: (r) => formatDate(new Date(r.issue_date)),
  },
  {
    key: "invoice_no",
    header: () => t`Invoice_no`,
    render: (r) => <ProformaNo row={r} />,
  },
  {
    key: "amount",
    header: () => t`Amount`,
    render: (r) => {
      return (
        <ColoredAmount value={r.stats.total_amount}>
          {currency.formatByWorkspace(r.stats.total_amount)}
        </ColoredAmount>
      );
    },
  },
];

export default observer(TableProformaInvoices);
